import { useEffect, useState } from 'react';
import { DeviceType } from '@repo/types';

declare global {
  var __SSR_DEVICE_TYPE__: string | undefined;
}

const deviceTypeMap: Record<string, DeviceType> = {
  mobile: DeviceType.mobile,
  tablet: DeviceType.tablet,
  desktop: DeviceType.desktop,
};

export const WIDE_DESKTOP_MIN_WIDTH = 1440;

/** How long the viewport must stay on one side of a breakpoint before `useDeviceType` reports it. */
export const DEVICE_TYPE_SETTLE_MS = 150;

export function getDeviceTypeFromWidth(): DeviceType {
  if (typeof window === 'undefined') return DeviceType.desktop;
  if (window.innerWidth < 768) return DeviceType.mobile;
  if (window.innerWidth < 1024) return DeviceType.tablet;
  return DeviceType.desktop;
}

// Track whether initial hydration is complete.
// During hydration, all components must return the SSR device type to avoid mismatches.
// After hydration, components use the actual window width.
let hydrated = typeof window === 'undefined' ? false : !(window as any).__SSR_DEVICE_TYPE__;

/**
 * Returns true if we are still in the initial hydration phase (before any useEffect has fired).
 * Exported so non-hook utilities like isMobileClient can also respect the hydration phase.
 */
export const isHydrating = () => !hydrated;

/**
 * Resolves the device type based on the current phase (SSR, hydration, or post-hydration).
 * Pure function — safe to call outside React for testing or non-hook contexts.
 */
export function resolveDeviceType(): DeviceType {
  if (typeof window === 'undefined') {
    return deviceTypeMap[globalThis.__SSR_DEVICE_TYPE__ ?? ''] ?? DeviceType.desktop;
  }
  if (!hydrated && (window as any).__SSR_DEVICE_TYPE__) {
    return deviceTypeMap[(window as any).__SSR_DEVICE_TYPE__] ?? getDeviceTypeFromWidth();
  }
  return getDeviceTypeFromWidth();
}

// One pair of breakpoint listeners and one settle timer for the whole page,
// however many components ask. Every subscriber hears the settled device type
// in the same callback, so React commits them all together.
const settledListeners = new Set<(deviceType: DeviceType) => void>();
// Set by the first subscriber, called by the last one to leave.
let stopWatchingBreakpoints: () => void;

function watchBreakpoints(): () => void {
  // Listen on the breakpoint boundaries themselves rather than every resize
  // pixel. The browser fires the `change` event only when the viewport
  // actually crosses 768px or 1024px, so resize is essentially free.
  const mobileTabletBoundary = window.matchMedia('(min-width: 768px)');
  const tabletDesktopBoundary = window.matchMedia('(min-width: 1024px)');

  // Read the width only once the viewport has stopped crossing boundaries.
  // A crossing that reverts within the window (desktop → mobile → desktop)
  // never reaches state: re-rendering for it would flip every device-keyed
  // subtree twice, and CmsPage's CrossFade goes `inert` for the flip, which
  // blurs whatever the visitor has focused. Chromium's element screenshots
  // resize the viewport to 1×1 for an instant, which is one such flip.
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  const handleChange = () => {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      const deviceType = getDeviceTypeFromWidth();
      settledListeners.forEach((listener) => listener(deviceType));
    }, DEVICE_TYPE_SETTLE_MS);
  };

  mobileTabletBoundary.addEventListener('change', handleChange);
  tabletDesktopBoundary.addEventListener('change', handleChange);

  return () => {
    clearTimeout(settleTimer);
    mobileTabletBoundary.removeEventListener('change', handleChange);
    tabletDesktopBoundary.removeEventListener('change', handleChange);
  };
}

function subscribeToSettledDeviceType(listener: (deviceType: DeviceType) => void): () => void {
  if (settledListeners.size === 0) stopWatchingBreakpoints = watchBreakpoints();
  settledListeners.add(listener);

  return () => {
    settledListeners.delete(listener);
    if (settledListeners.size === 0) stopWatchingBreakpoints();
  };
}

export const useDeviceType = () => {
  const [deviceType, setDeviceType] = useState(resolveDeviceType);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      hydrated = true;
      return;
    }

    // Reconcile once with the actual width — handles cases where the SSR
    // device type (e.g. "desktop" from a macOS PWA User-Agent) differs from
    // the width-based type (e.g. "mobile" in a narrow PWA window).
    hydrated = true;
    setDeviceType(getDeviceTypeFromWidth());

    return subscribeToSettledDeviceType(setDeviceType);
  }, []);

  return deviceType;
};

/**
 * Returns true when the viewport is at or above the wide-desktop threshold
 * (1440px). Used by features (e.g. sidebar ads) that should only render on
 * genuinely wide viewports — wider than the regular desktop layout breakpoint.
 */
export const useIsWideDesktop = () => {
  const [isWideDesktop, setIsWideDesktop] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth >= WIDE_DESKTOP_MIN_WIDTH;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const query = window.matchMedia(`(min-width: ${WIDE_DESKTOP_MIN_WIDTH}px)`);
    setIsWideDesktop(query.matches);

    const handleChange = (event: MediaQueryListEvent) => setIsWideDesktop(event.matches);
    query.addEventListener('change', handleChange);

    return () => query.removeEventListener('change', handleChange);
  }, []);

  return isWideDesktop;
};
